import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  makeStyles,
} from '@fluentui/react-components';
import { personAttributeSchema, type Person, type PersonAttribute } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { updatePerson, type ApiError } from '@/lib/api';
import { answerLabels, answerOf, attributeAnswers, attributeQuestions } from './attributes.js';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  question: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
});

export interface AttributeEditorProps {
  readonly person: Person;
  readonly canEdit: boolean;
}

/**
 * The questions that decide which conditional requirements apply to someone.
 *
 * Until this existed nothing could set an attribute, so seven requirements could never
 * fire — including the one gating aides, which left every home health aide permanently at
 * "Needs review" with no way for HR to clear them.
 *
 * The three-way control is the point. A checkbox would collapse "not answered" into "no",
 * and those mean opposite things to the engine: an unanswered question on a blocking
 * requirement holds the person, where a "no" clears them.
 */
export function AttributeEditor({ person, canEdit }: AttributeEditorProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PersonAttribute | null>(null);

  const save = useMutation({
    mutationFn: (attributes: Record<string, string | undefined>) =>
      updatePerson(person.id, { attributes }),
    onSettled: async () => {
      setPending(null);
      await queryClient.invalidateQueries({ queryKey: ['person', person.id] });
      // The checklist is derived from these answers, so it must be refetched with them.
      await queryClient.invalidateQueries({ queryKey: ['checklist', person.id] });
    },
  });

  function answer(attribute: PersonAttribute, value: string) {
    setPending(attribute);
    const next: Record<string, string | undefined> = { ...person.attributes };
    // "Not answered" removes the key rather than storing a third string: absence IS the
    // unanswered state everywhere else in the system, and storing a sentinel would give
    // the same fact two representations.
    if (value === 'unanswered') delete next[attribute];
    else next[attribute] = value;
    save.mutate(next);
  }

  const error = save.error as (Error & Partial<ApiError>) | null;

  return (
    <div className={styles.panel}>
      <div>
        <Text weight="semibold">About this person</Text>
        <br />
        <Caption1>
          These answers decide which requirements apply. Leaving one unanswered is not the same
          as answering “no” — an unanswered question holds someone at “Needs review”.
        </Caption1>
      </div>

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      )}

      {personAttributeSchema.options.map((attribute) => {
        const details = attributeQuestions[attribute];
        const current = answerOf(person.attributes[attribute]);
        return (
          <Field key={attribute} label={details.question} hint={details.helpText}>
            <div className={styles.row}>
              <RadioGroup
                layout="horizontal"
                value={current}
                disabled={!canEdit || save.isPending}
                onChange={(_, d) => answer(attribute, d.value)}
              >
                {attributeAnswers.map((option) => (
                  <Radio key={option} value={option} label={answerLabels[option]} />
                ))}
              </RadioGroup>
              {pending === attribute && save.isPending ? <Spinner size="tiny" /> : null}
            </div>
          </Field>
        );
      })}
    </div>
  );
}
